import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Container, PageTitle, GameContainer, Button, FlexContainer } from '../../styles/StyledComponents';
import { useGameContext } from '../../context/GameContext';

const FactoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 2rem 0;
`;

const WordDisplay = styled.div`
  font-size: 3rem;
  text-align: center;
  margin: 1rem 0;
  background-color: #f9f9f9;
  padding: 1rem 2rem;
  border-radius: var(--border-radius);
  box-shadow: var(--box-shadow);
  min-width: 300px;
`;

const ConveyorBelt = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 2rem 0;
  background-color: #3e3e3e;
  padding: 1rem;
  border-radius: var(--border-radius);
  width: 100%;
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 10px;
    background: repeating-linear-gradient(
      90deg,
      #333333,
      #333333 20px,
      #555555 20px,
      #555555 40px
    );
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 10px;
    background: repeating-linear-gradient(
      90deg,
      #333333,
      #333333 20px,
      #555555 20px,
      #555555 40px
    );
  }
`;

const PhonicsSlot = styled.div`
  width: 70px;
  height: 70px;
  margin: 0 0.5rem;
  background-color: white;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.2);
  position: relative;

  &.empty {
    background-color: rgba(255, 255, 255, 0.5);
    box-shadow: inset 0 0 10px rgba(0, 0, 0, 0.3);
  }

  &.incorrect {
    background-color: var(--error-color);
    color: white;
  }

  &.correct {
    background-color: var(--success-color);
    color: white;
  }

  &.highlight {
    animation: pulse 1s infinite;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.4);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
    }
  }
`;

const PhonicsButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin: 2rem 0;
`;

const PhonicsButton = styled.button`
  width: 70px;
  height: 70px;
  margin: 0.5rem;
  font-size: 2rem;
  font-weight: bold;
  background-color: white;
  border: 3px solid var(--secondary-color);
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 0 var(--secondary-color);

  &:hover {
    background-color: var(--secondary-color);
    color: white;
  }

  &:active {
    transform: translateY(4px);
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImageContainer = styled.div`
  width: 200px;
  height: 200px;
  background-color: #f9f9f9;
  border-radius: var(--border-radius);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  font-size: 6rem;
  box-shadow: var(--box-shadow);
`;

const FeedbackMessage = styled.div<{ isCorrect: boolean }>`
  font-size: 1.5rem;
  text-align: center;
  padding: 1rem;
  margin: 1rem 0;
  border-radius: var(--border-radius);
  background-color: ${props => props.isCorrect ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)'};
  color: ${props => props.isCorrect ? 'var(--success-color)' : 'var(--error-color)'};
`;

const ScoreDisplay = styled.div`
  font-size: 1.2rem;
  text-align: right;
  margin-bottom: 1rem;

  span {
    font-weight: bold;
    color: var(--primary-color);
  }
`;

const InstructionText = styled.p`
  font-size: 1.3rem;
  text-align: center;
  margin-bottom: 1.5rem;
`;

interface WordData {
  word: string;
  phonics: string[];
  image: string;
}

const words: WordData[] = [
  { word: 'CAT', phonics: ['C', 'A', 'T'], image: '🐱' },
  { word: 'DOG', phonics: ['D', 'O', 'G'], image: '🐶' },
  { word: 'FISH', phonics: ['F', 'I', 'SH'], image: '🐠' },
  { word: 'BIRD', phonics: ['B', 'IR', 'D'], image: '🐦' },
  { word: 'FROG', phonics: ['F', 'R', 'O', 'G'], image: '🐸' },
  { word: 'SHIP', phonics: ['SH', 'I', 'P'], image: '🚢' },
  { word: 'STAR', phonics: ['S', 'T', 'AR'], image: '⭐' },
  { word: 'TREE', phonics: ['T', 'R', 'EE'], image: '🌳' },
  { word: 'DUCK', phonics: ['D', 'U', 'CK'], image: '🦆' },
  { word: 'BALL', phonics: ['B', 'A', 'LL'], image: '⚽' }
];

// All possible phonics sounds for the buttons
const allPhonics = [
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
  'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
  'SH', 'CH', 'TH', 'WH', 'PH', 'EE', 'OO', 'AR', 'OR', 'ER', 'IR', 'UR', 'OU', 'OW', 'OI', 'AY', 'AI', 'EA'
];

const PhonicsFactory: React.FC = () => {
  const [currentWord, setCurrentWord] = useState<WordData>(getRandomWord());
  const [phonicsOptions, setPhonicsOptions] = useState<string[]>([]);
  const [userPhonics, setUserPhonics] = useState<string[]>([]);
  const [currentSlot, setCurrentSlot] = useState<number>(0);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [score, setScore] = useState<number>(0);
  const { updateProgress } = useGameContext();

  function getRandomWord(): WordData {
    return words[Math.floor(Math.random() * words.length)];
  }

  // Generate phonics options when the current word changes
  useEffect(() => {
    const options = [...currentWord.phonics];

    // Add some incorrect but plausible options
    while (options.length < 8) {
      const randomPhonic = allPhonics[Math.floor(Math.random() * allPhonics.length)];
      if (!options.includes(randomPhonic)) {
        options.push(randomPhonic);
      }
    }

    // Shuffle options
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }

    setPhonicsOptions(options);
    setUserPhonics(Array(currentWord.phonics.length).fill(''));
    setCurrentSlot(0);
    setIsCorrect(null);
  }, [currentWord]);

  const handlePhonicClick = (phonic: string) => {
    if (currentSlot >= currentWord.phonics.length || isCorrect !== null) return;

    const newUserPhonics = [...userPhonics];
    newUserPhonics[currentSlot] = phonic;
    setUserPhonics(newUserPhonics);

    playSound('click');

    // Move to next slot
    if (currentSlot < currentWord.phonics.length - 1) {
      setCurrentSlot(currentSlot + 1);
    } else {
      // Check if the word is complete
      checkWord(newUserPhonics);
    }
  };

  const checkWord = (phonics: string[]) => {
    // Check if the user's phonics match the word's phonics
    const correct = phonics.every((phonic, index) => phonic === currentWord.phonics[index]);
    setIsCorrect(correct);

    if (correct) {
      setScore(prevScore => prevScore + 1);
      playSound('success');

      // Move to next word after 2 seconds
      setTimeout(() => {
        setCurrentWord(getRandomWord());
      }, 2000);
    } else {
      playSound('error');

      // Show correct phonics after a short delay
      setTimeout(() => {
        setUserPhonics(currentWord.phonics);
      }, 1000);
    }

    // Save progress to context
    updateProgress('phonics-factory', score + (correct ? 1 : 0));
  };

  const resetWord = () => {
    setUserPhonics(Array(currentWord.phonics.length).fill(''));
    setCurrentSlot(0);
    setIsCorrect(null);
  };

  const playSound = (type: 'success' | 'error' | 'click') => {
    // In a real implementation, we would play actual sounds here
    console.log(`Playing ${type} sound`);
  };

  const playPhonic = (phonic: string) => {
    // In a real implementation, we would play the phonic sound here
    console.log(`Playing phonic sound: ${phonic}`);
  };

  return (
    <Container>
      <PageTitle>Phonics Factory</PageTitle>

      <GameContainer>
        <FlexContainer justify="space-between">
          <Link to="/">
            <Button variant="secondary">Back to Games</Button>
          </Link>

          <ScoreDisplay>
            Score: <span>{score}</span>
          </ScoreDisplay>
        </FlexContainer>

        <InstructionText>
          Click on the letter sounds to build the word!
        </InstructionText>

        <FactoryContainer>
          <ImageContainer>{currentWord.image}</ImageContainer>

          <WordDisplay>
            {userPhonics.map((phonic, index) => phonic).join('')}
          </WordDisplay>

          <ConveyorBelt>
            {currentWord.phonics.map((phonic, index) => (
              <PhonicsSlot
                key={index}
                className={`
                  ${!userPhonics[index] ? 'empty' : ''}
                  ${isCorrect === true && userPhonics[index] ? 'correct' : ''}
                  ${isCorrect === false && userPhonics[index] ? (userPhonics[index] === currentWord.phonics[index] ? 'correct' : 'incorrect') : ''}
                  ${currentSlot === index && isCorrect === null ? 'highlight' : ''}
                `}
                onClick={() => playPhonic(currentWord.phonics[index])}
              >
                {userPhonics[index] || '?'}
              </PhonicsSlot>
            ))}
          </ConveyorBelt>

          <PhonicsButtons>
            {phonicsOptions.map((phonic, index) => (
              <PhonicsButton
                key={index}
                onClick={() => handlePhonicClick(phonic)}
                disabled={currentSlot >= currentWord.phonics.length || isCorrect !== null}
              >
                {phonic}
              </PhonicsButton>
            ))}
          </PhonicsButtons>

          <Button
            variant="secondary"
            onClick={resetWord}
            disabled={isCorrect === true}
          >
            Reset
          </Button>

          {isCorrect !== null && (
            <FeedbackMessage isCorrect={isCorrect}>
              {isCorrect
                ? '✨ Great job! You built the word! ✨'
                : 'Try again! Some sounds are not correct.'}
            </FeedbackMessage>
          )}
        </FactoryContainer>
      </GameContainer>
    </Container>
  );
};

export default PhonicsFactory;
